import { Button, Typography } from 'antd';
import { Plus } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import React from 'react';
import type { KanbanTask, KanbanTaskStatus } from '../../stores/KanbanTaskStores';
import type { TaskStore } from '../../stores/kanban/TaskStore';
import type { Employee } from '../../models/Employee';
import Column from './Column';
import type { BoardDnd } from './useBoardDnd';
import type { QuickAdd } from './useQuickAdd';

const { Title, Paragraph } = Typography;

export type BoardProps = {
  store: TaskStore;
  /** Sorted by index by the page. */
  statuses: KanbanTaskStatus[];
  /** Already filtered and sorted, keyed by status id. */
  tasksByStatus: Record<string, KanbanTask[]>;
  isReadOnly: boolean;
  /** Anonymous (public share-link) board; threaded down to `Column` to hide its delete menu item. */
  isAnonymous: boolean;
  dnd: BoardDnd;
  quickAdd: QuickAdd;
  onOpenTask: (task: KanbanTask) => void;
  onAddStatus: () => void;
  onDeleteStatus: (statusId: string) => void;
  t: (key: string) => string;
  assignableUsers?: Employee[];
  baseCloudinary?: string;
};

const Board: React.FC<BoardProps> = observer(
  ({
    store,
    statuses,
    tasksByStatus,
    isReadOnly,
    isAnonymous,
    dnd,
    quickAdd,
    onOpenTask,
    onAddStatus,
    onDeleteStatus,
    t,
    assignableUsers,
    baseCloudinary,
  }) => {
    if (statuses.length === 0) {
      return (
        <div className="kanban-board">
          <div className="empty-columns">
            <div className="empty-columns-message">
              <Title level={4}>{t('NoColumnsYet')}</Title>
              <Paragraph className="empty-description">{t('CreateYourFirstColumnToGetStarted')}</Paragraph>
              <Button
                type="primary"
                size="large"
                icon={<Plus size={16} />}
                onClick={onAddStatus}
                className="empty-add-column-button hover-effect"
                disabled={isReadOnly}
              >
                {t('AddStatus')}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="kanban-board">
        <div className="columns-container">
          {statuses.map((status, index) => (
            <Column
              key={status.id}
              status={status}
              index={index}
              tasks={tasksByStatus[status.id] ?? []}
              store={store}
              isReadOnly={isReadOnly}
              isAnonymous={isAnonymous}
              dnd={dnd}
              quickAdd={quickAdd}
              onOpenTask={onOpenTask}
              onDeleteStatus={onDeleteStatus}
              t={t}
              assignableUsers={assignableUsers}
              baseCloudinary={baseCloudinary}
            />
          ))}

          <div className="add-column-wrapper">
            <Button
              type="dashed"
              icon={<Plus size={16} />}
              onClick={onAddStatus}
              className="add-column-button hover-effect"
              disabled={isReadOnly}
            >
              {t('AddStatus')}
            </Button>
          </div>
        </div>
      </div>
    );
  },
);

export default Board;
